/**
 * Values Component
 * 
 * Displays core company values with imagery in responsive grid layout.
 * 
 * DESIGN STANDARDS:
 * - 3-column responsive grid (lg:3, md:2, mobile:1)
 * - Image-top card design with gradient overlays
 * - Consistent spacing: py-20 section padding
 * - Scroll-triggered animations with staggered card reveals
 * - Uses ValueCard component for consistent styling
 * 
 * Values Include:
 * - Trust & Partnership
 * - Safety & Quality
 * - Innovation with Discipline
 * - People-Centered Leadership
 * - Global Mindset
 * - Sustainable Excellence
 * 
 * @component
 */

import { memo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { companyValues } from "@/data/values";
import { ValueCard } from "@/components/common/ValueCard";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { SectionHeader } from "@/components/common/SectionHeader";

const Values = () => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <section 
      className="py-20 bg-background" 
      ref={ref} 
      aria-labelledby="values-heading"
    >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            
            {/* Section Header */}
            <div 
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <SectionHeader
                eyebrow="Our Values"
                title="The principles that guide every decision"
                description="Built on trust, safety, and sustainable excellence"
              />
            </div>
            
            {/* Values Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {companyValues.map((value, index) => (
                <div
                  key={index}
                  className={`transition-all duration-700 flex ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: isVisible ? `${index * 100}ms` : '0ms' }}
                >
                  <ValueCard {...value} />
                </div>
              ))}
            </div>
            
            {/* CTA Link to Our Story - Important for SEO crawlability */}
            <div 
              className={`text-center mt-12 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: isVisible ? '700ms' : '0ms' }}
            >
              <Link 
                to="/our-story" 
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 font-medium transition-colors group"
              >
                Discover our 25+ year journey in maritime logistics
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
  );
};

export default memo(Values);
